/* eslint-disable max-len */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import apiClient from '../../common/functions/api_axios_instance.function';
import { handleApiError } from '../../common/functions/errorHandler.function';
import { getVendorInvoices } from './functions/get_vendor_invoices.function';
import { getNonPendingVendorInvoices } from './functions/get_non_pending_vendor_invoices.function';
import {
  getDocumentBlob,
  revokeBlobUrl,
} from '../ApprovalPersonsInvoiceManagement/functions/get_document_blob.function';
import VendorInvoiceHeader from './components/vendor_invoice_header.component';
import InvoiceDetailsCard from '../ApprovalPersonsInvoiceManagement/components/invoice_details_card.component';
import BreakdownTable from '../ApprovalPersonsInvoiceManagement/components/breakdown_table.component';
import DocumentsList from '../ApprovalPersonsInvoiceManagement/components/documents_list.component';
import InvoicePreview from '../ApprovalPersonsInvoiceManagement/components/invoice_preview.component';
import DocumentPreview from '../ApprovalPersonsInvoiceManagement/components/document_preview.component';
import { buildPoInvoiceSummary } from '../ApprovalPersonsInvoiceManagement/utils/po_invoice_summary.util';

const VendorInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Support both legacy :invoiceNumber param and new splat route
  const rawParam = params.invoiceNumber || params['*'] || '';
  let decodedInvoiceNumber = rawParam;
  try {
    decodedInvoiceNumber = decodeURIComponent(rawParam);
  } catch (e) {
    console.warn('Failed to decode invoiceNumber param, using raw value:', rawParam, e);
    decodedInvoiceNumber = rawParam;
  }
  const passedInvoiceData = location.state?.invoiceData; // Get invoice data from navigation state

  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Vendor Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [invoiceData, setInvoiceData] = useState(null);
  const [breakdownData, setBreakdownData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [poInvoicesForSummary, setPoInvoicesForSummary] = useState([]);
  const [rawInvoiceSource, setRawInvoiceSource] = useState(passedInvoiceData || null);

  // Get current invoice number from URL params
  const currentInvoiceNumber = decodedInvoiceNumber;

  // Helper function to map invoice data
  const mapInvoiceData = useCallback(
    (invoiceData) => {
      const formatDate = (dateString) => {
        if (!dateString) {
          return '';
        }
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
        } catch (e) {
          return dateString;
        }
      };

      const poSummary = buildPoInvoiceSummary({
        rawInvoice: invoiceData,
        poInvoicesList: poInvoicesForSummary,
      });

      const mappedInvoiceData = {
        invoiceNumber: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
        title: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
        invoiceNo: invoiceData.invoice_number || invoiceData.invoiceNumber || '',
        category: invoiceData.category || '',
        vendorName: invoiceData.vendor || '',
        invoiceDate: formatDate(invoiceData.created_at || invoiceData.invoice_date),
        region: invoiceData.region || '',
        poNumber: invoiceData.po_number || invoiceData.poNumber || '',
        poStatus: 'Active',
        approvalStatus: invoiceData.status
          ? invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1).toLowerCase()
          : '',
        status: invoiceData.status || '',
        vendorRemarks: invoiceData.vendor_remarks || invoiceData.vendorRemarks || '',
        invoiceType: invoiceData.invoice_type || invoiceData.invoiceType || '',
        poTotalAmountFormatted: poSummary.poTotalAmountFormatted,
        alreadyClaimedAmountFormatted: poSummary.alreadyClaimedAmountFormatted,
        poInvoicesCount: poSummary.poInvoicesCount,
        reasonOfRejection: poSummary.reasonOfRejection,
      };

      const mappedBreakdownData = (invoiceData.rings_data || []).map((ring) => ({
        name: ring.ringName || ring.sectionName || '',
        milestone: ring.paymentMilestone || ring.paymentRingMilestone || '',
        length: ring.length || '',
        unitRate: ring.unitRate || '',
        invoiceAmount: ring.amount || '',
        // Preserve category/type so we can show On-Net/Off-Net totals in the breakdown table
        category: ring.category || ring.type || '',
      }));

      const mappedDocuments = [];
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';

      // Add PO file if exists
      if (invoiceData.po_file_path) {
        const fileName = invoiceData.po_file_path.split('/').pop() || 'PO File.pdf';
        mappedDocuments.push({
          id: 'po-file',
          name: 'PO File',
          fileName: fileName,
          filePath: invoiceData.po_file_path,
          url: `${apiBaseUrl}/${invoiceData.po_file_path}`,
          type: 'application/pdf',
        });
      }

      // Add other documents
      if (invoiceData.other_documents_paths && Array.isArray(invoiceData.other_documents_paths)) {
        invoiceData.other_documents_paths.forEach((docPath, index) => {
          const fileName = docPath.split('/').pop() || `Document ${index + 1}`;
          const lowerPath = docPath.toLowerCase();
          const lowerFileName = fileName.toLowerCase();

          // Determine file type based on extension
          let fileType = 'application/octet-stream';
          if (lowerPath.endsWith('.pdf') || lowerFileName.endsWith('.pdf')) {
            fileType = 'application/pdf';
          } else if (lowerPath.endsWith('.xlsx') || lowerFileName.endsWith('.xlsx')) {
            fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          } else if (lowerPath.endsWith('.xls') || lowerFileName.endsWith('.xls')) {
            fileType = 'application/vnd.ms-excel';
          } else if (lowerPath.endsWith('.csv') || lowerFileName.endsWith('.csv')) {
            fileType = 'text/csv';
          } else if (lowerPath.endsWith('.msg') || lowerFileName.endsWith('.msg')) {
            fileType = 'application/vnd.ms-outlook';
          }

          mappedDocuments.push({
            id: `other-doc-${index}`,
            name: fileName,
            fileName: fileName,
            filePath: docPath,
            // Use the new authenticated endpoint for serving documents
            // Note: Backend route is mounted at /vendorInvoiceManagement
            url: `${apiBaseUrl}/vendorInvoiceManagement/invoice/document/${docPath}`,
            type: fileType,
          });
        });
      }

      return { mappedInvoiceData, mappedBreakdownData, mappedDocuments };
    },
    [poInvoicesForSummary]
  );

  // Load all vendor invoices for this PO (count + claimed amount)
  useEffect(() => {
    const poNum = (
      rawInvoiceSource?.po_number ||
      rawInvoiceSource?.poNumber ||
      passedInvoiceData?.po_number ||
      passedInvoiceData?.poNumber ||
      ''
    ).trim();
    if (!poNum) {
      setPoInvoicesForSummary([]);
      return undefined;
    }

    let cancelled = false;
    const extractInvoiceList = (response) => {
      if (Array.isArray(response?.response)) {
        return response.response;
      }
      if (Array.isArray(response?.data)) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    };

    const loadPoInvoices = async () => {
      try {
        const [pendingRes, nonPendingRes] = await Promise.all([
          getVendorInvoices(),
          getNonPendingVendorInvoices(),
        ]);
        const combined = [...extractInvoiceList(pendingRes), ...extractInvoiceList(nonPendingRes)];
        const seen = new Set();
        const unique = combined.filter((inv) => {
          const key = inv.id ?? inv.invoice_number ?? inv.invoiceNumber;
          if (key == null || seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });
        const forPo = unique.filter(
          (inv) => (inv.po_number || inv.poNumber || '').trim() === poNum
        );
        if (!cancelled) {
          setPoInvoicesForSummary(forPo);
        }
      } catch (err) {
        if (!cancelled) {
          setPoInvoicesForSummary([]);
        }
      }
    };

    loadPoInvoices();
    return () => {
      cancelled = true;
    };
  }, [
    rawInvoiceSource?.po_number,
    rawInvoiceSource?.poNumber,
    passedInvoiceData?.po_number,
    passedInvoiceData?.poNumber,
  ]);

  // Load invoice data from passed state or fetch from API
  useEffect(() => {
    const loadInvoiceData = async () => {
      setLoading(true);
      setError(null);

      try {
        let sourceInvoice = passedInvoiceData || null;

        if (!sourceInvoice) {
          const [pendingRes, nonPendingRes] = await Promise.all([
            getVendorInvoices(),
            getNonPendingVendorInvoices(),
          ]);
          const extractList = (response) => {
            if (Array.isArray(response?.response)) {
              return response.response;
            }
            if (Array.isArray(response?.data)) {
              return response.data;
            }
            if (Array.isArray(response)) {
              return response;
            }
            return [];
          };
          const combined = [...extractList(pendingRes), ...extractList(nonPendingRes)];
          const seen = new Set();
          const invoices = combined.filter((inv) => {
            const key = inv.id ?? inv.invoice_number ?? inv.invoiceNumber;
            if (key == null || seen.has(key)) {
              return false;
            }
            seen.add(key);
            return true;
          });

          sourceInvoice =
            invoices.find(
              (inv) =>
                (inv.invoice_number || inv.invoiceNumber) === decodedInvoiceNumber ||
                inv.id?.toString() === decodedInvoiceNumber
            ) || null;
        }

        if (sourceInvoice) {
          setRawInvoiceSource(sourceInvoice);
          const { mappedInvoiceData, mappedBreakdownData, mappedDocuments } =
            mapInvoiceData(sourceInvoice);

          setInvoiceData(mappedInvoiceData);
          setBreakdownData(mappedBreakdownData);
          setDocuments(mappedDocuments);
          setInvoicePreviewData(null);

          if (mappedDocuments.length > 0) {
            setSelectedDocumentIndex(0);
            setSelectedDocument(mappedDocuments[0]);
            setShowDocumentPreview(true);
          }
        } else {
          setError('Invoice not found. Please check the invoice number.');
        }
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };

    loadInvoiceData();
  }, [decodedInvoiceNumber, passedInvoiceData, mapInvoiceData]);

  // Re-map when PO invoice list loads (updates PO Summary counts/amounts)
  useEffect(() => {
    if (!rawInvoiceSource) {
      return;
    }
    const { mappedInvoiceData, mappedBreakdownData, mappedDocuments } =
      mapInvoiceData(rawInvoiceSource);
    setInvoiceData(mappedInvoiceData);
    setBreakdownData(mappedBreakdownData);
    setDocuments(mappedDocuments);
  }, [rawInvoiceSource, mapInvoiceData, poInvoicesForSummary]);

  const handleBack = () => {
    navigate('/vendorInvoiceManagement');
  };

  const handleDocumentClick = async (index, document) => {
    console.log('Document clicked:', index, document);

    // Revoke previous blob URL if it exists
    const previousBlobUrl = documentBlobUrl;
    if (previousBlobUrl) {
      revokeBlobUrl(previousBlobUrl);
    }

    // Clear previous state
    setDocumentBlobUrl(null);
    setLoadingDocument(true);
    setSelectedDocumentIndex(index);
    setSelectedDocument(document);
    setShowDocumentPreview(true);

    try {
      // If document has a blobUrl already, use it
      if (document.blobUrl) {
        setDocumentBlobUrl(document.blobUrl);
        setLoadingDocument(false);
        return;
      }

      // For Excel/CSV/MSG files, we need to fetch as blob for parsing
      // For PDFs, we can use direct URL or blob
      const isExcelOrCSVOrMsg =
        document.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        document.type === 'application/vnd.ms-excel' ||
        document.type === 'text/csv' ||
        document.type === 'application/vnd.ms-outlook' ||
        /\.(xlsx|xls|csv|msg)$/i.test(document.fileName || document.name || '');

      // If document has a direct URL and it's not Excel/CSV/MSG, use it directly
      if ((document.url || document.documentUrl || document.fileUrl) && !isExcelOrCSVOrMsg) {
        setLoadingDocument(false);
        return;
      }

      // For documents with real backend IDs (not frontend-generated), fetch blob from backend
      // Frontend-generated IDs like 'other-doc-X' or 'po-file' should use direct URLs
      const isRealBackendId =
        document.id && !document.id.startsWith('other-doc-') && !document.id.startsWith('po-file');

      if (isRealBackendId && currentInvoiceNumber) {
        const blobUrl = await getDocumentBlob(currentInvoiceNumber, document.id);
        setDocumentBlobUrl(blobUrl);
      } else if (isExcelOrCSVOrMsg && (document.url || document.documentUrl || document.fileUrl)) {
        // For Excel/CSV/MSG files with direct URL, we'll let DocumentPreview handle it
        setLoadingDocument(false);
        return;
      } else if (document.url || document.documentUrl || document.fileUrl) {
        // For other files with direct URL, use it directly
        setLoadingDocument(false);
        return;
      }
    } catch (error) {
      console.error('Error loading document:', error);
      handleApiError(error);
    } finally {
      setLoadingDocument(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentBlobUrl) {
        revokeBlobUrl(documentBlobUrl);
      }
    };
  }, [documentBlobUrl]);

  if (loading) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !invoiceData) {
    return (
      <DashboardLayout padding={true}>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              margin: '0 auto',
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
            }}
          >
            <Alert severity="error">{error || 'Invoice not found'}</Alert>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout padding={true}>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            margin: '0 auto',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
          }}
        >
          {/* Header */}
          <VendorInvoiceHeader invoiceNumber={invoiceData.invoiceNumber} onBack={handleBack} />

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <InvoiceDetailsCard invoiceData={invoiceData} hideApproverFields />
              <BreakdownTable
                breakdownData={breakdownData}
                nameColumnLabel={
                  (
                    passedInvoiceData?.invoice_type ||
                    passedInvoiceData?.invoice_details?.invoice_type ||
                    invoiceData?.invoiceType ||
                    ''
                  )
                    .toString()
                    .toUpperCase()
                    .replace(/-/g, '_') === 'NON_FTTS'
                    ? 'Section Name'
                    : 'Ring Name'
                }
              />
              <DocumentsList
                documents={documents}
                selectedIndex={selectedDocumentIndex}
                onDocumentClick={handleDocumentClick}
              />
            </Grid>

            {/* Right Column - Show Document Preview or Invoice Preview */}
            <Grid item xs={12} md={6}>
              {showDocumentPreview && selectedDocument ? (
                <DocumentPreview
                  key={`doc-preview-${selectedDocumentIndex}-${selectedDocument.id || selectedDocumentIndex}-${documentBlobUrl || selectedDocument.url || ''}`}
                  documentUrl={(() => {
                    // Determine if this is an Excel/CSV/MSG file
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    // Get the base URL
                    let baseUrl =
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      // Only construct URL from ID if it's a real backend ID (not frontend-generated like 'other-doc-X')
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/vendor/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null);

                    // For Excel/CSV/MSG files, add ?format=pdf to convert to PDF for preview
                    if (isExcelOrCSVOrMsg && baseUrl && !baseUrl.includes('blob:')) {
                      const separator = baseUrl.includes('?') ? '&' : '?';
                      baseUrl = `${baseUrl}${separator}format=pdf`;
                    }

                    return baseUrl;
                  })()}
                  downloadUrl={(() => {
                    // For downloads, use the original file URL (without PDF conversion)
                    return (
                      documentBlobUrl ||
                      selectedDocument.blobUrl ||
                      selectedDocument.url ||
                      selectedDocument.documentUrl ||
                      selectedDocument.fileUrl ||
                      (selectedDocument.id &&
                      !selectedDocument.id.startsWith('other-doc-') &&
                      !selectedDocument.id.startsWith('po-file')
                        ? `${process.env.REACT_APP_API_URL}/vendor/invoice/${currentInvoiceNumber}/documents/${selectedDocument.id}`
                        : null)
                    );
                  })()}
                  documentName={
                    selectedDocument.name ||
                    selectedDocument.fileName ||
                    selectedDocument.documentName ||
                    'Document'
                  }
                  documentType={(() => {
                    // For Excel/CSV/MSG files being previewed as PDF, return PDF type
                    const isExcelOrCSVOrMsg =
                      selectedDocument.type ===
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      selectedDocument.type === 'application/vnd.ms-excel' ||
                      selectedDocument.type === 'text/csv' ||
                      selectedDocument.type === 'application/vnd.ms-outlook' ||
                      /\.(xlsx|xls|csv|msg)$/i.test(
                        selectedDocument.fileName || selectedDocument.name || ''
                      );

                    if (isExcelOrCSVOrMsg) {
                      return 'application/pdf'; // Treat as PDF for preview
                    }
                    return selectedDocument.type || selectedDocument.mimeType || 'application/pdf';
                  })()}
                  isLoading={loadingDocument}
                />
              ) : (
                invoicePreviewData && <InvoicePreview invoicePreviewData={invoicePreviewData} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default VendorInvoiceDetails;
